import { useEffect, useRef, useState } from 'react';
import ChatGateway, { Session } from '../../api/gateways/ChatGateway';
import { useToast } from '../../components/toast';
import { useUser } from '../../store/auth/AuthSelectors';
import { screens } from '../Navigation.types';
import { useNavigate } from 'react-router';

const getInitialExpandedState = (): boolean => {
	if (typeof window === 'undefined') return true;
	return window.innerWidth > 768;
};

export const useChatSidebar = (onClearConversation?: () => void) => {
	const [isExpanded, setIsExpanded] = useState(getInitialExpandedState);
	const [sessions, setSessions] = useState<Session[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
	const [editValue, setEditValue] = useState<string>('');
	const [, setOriginalValue] = useState<string>('');
	const [isRenaming, setIsRenaming] = useState<boolean>(false);
	const [renameError, setRenameError] = useState<string | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
	const user = useUser();
	const { danger, success } = useToast();
	const navigate = useNavigate();
	/** Ignore stale GET /sessions responses (e.g. initial load finishing after delete + refresh). */
	const sessionsListFetchGen = useRef(0);

	const normalizeSessionsResponse = (response: unknown): Session[] => {
		if (
			response &&
			typeof response === 'object' &&
			'data' in response &&
			Array.isArray((response as { data: unknown }).data)
		) {
			return (response as { data: Session[] }).data;
		}
		return [];
	};

	useEffect(() => {
		if (!user?.id) return;
		const gen = ++sessionsListFetchGen.current;
		const fetchSessions = async () => {
			setIsLoading(true);
			try {
				const response = await ChatGateway.api.getAllSessions(user.id);
				if (gen !== sessionsListFetchGen.current) {
					return;
				}
				setSessions(normalizeSessionsResponse(response));
			} catch (error: any) {
				console.error('Error fetching sessions:', error);
				if (gen !== sessionsListFetchGen.current) {
					return;
				}
				if (error?.statusCode === 401) {
					danger('Unknown error. Redirecting to home.');
					navigate(screens.root, { replace: true });
					return;
				}
				setSessions([]);
			} finally {
				if (gen === sessionsListFetchGen.current) {
					setIsLoading(false);
				}
			}
		};
		fetchSessions();
	}, [user?.id]);

	useEffect(() => {
		if (renameError) {
			danger(renameError);
		}
	}, [renameError, danger]);

	const toggleSidebar = () => {
		setIsExpanded(!isExpanded);
	};

	const totalMessages = sessions.reduce(
		(sum, session) => sum + session.messageCount,
		0
	);

	const totalTokens = sessions.reduce(
		(sum, session) => sum + (session.totalTokens || 0),
		0
	);

	const refreshSessions = async (): Promise<void> => {
		if (!user?.id) return;

		const gen = ++sessionsListFetchGen.current;
		try {
			const response = await ChatGateway.api.getAllSessions(user.id);
			if (gen !== sessionsListFetchGen.current) {
				return;
			}
			setSessions(normalizeSessionsResponse(response));
		} catch (error: any) {
			console.error('Error refreshing sessions:', error);
			if (gen !== sessionsListFetchGen.current) {
				return;
			}
			if (error?.statusCode === 401) {
				danger('Unknown error. Redirecting to home.');
				navigate(screens.root, { replace: true });
				return;
			}
		} finally {
			// Must clear loading when this fetch wins: ChatBotScreen calls refreshSessions when
			// sessionId hydrates from URL, which bumps gen while the initial [user?.id] load is
			// still in flight—then the initial fetch's finally skips setIsLoading(false).
			if (gen === sessionsListFetchGen.current) {
				setIsLoading(false);
			}
		}
	};

	const getErrorMessage = (error: any): string => {
		if (error?.error) {
			return error.error;
		}
		if (error?.statusCode === 400) {
			return 'Invalid session name. Please check the requirements.';
		}
		if (error?.statusCode === 403) {
			return 'You do not have permission to rename this session';
		}
		if (error?.statusCode === 404) {
			return 'Session not found. Please refresh and try again.';
		}
		if (error?.statusCode === 500) {
			return 'Server error. Please try again later.';
		}
		return 'Failed to rename session. Please try again.';
	};

	const startRename = (sessionId: string, currentName: string) => {
		setEditingSessionId(sessionId);
		setEditValue(currentName);
		setOriginalValue(currentName);
		setRenameError(null);
	};

	const cancelRename = () => {
		setEditingSessionId(null);
		setEditValue('');
		setOriginalValue('');
		setRenameError(null);
	};

	const saveRename = async (
		sessionId: string,
		newName: string
	): Promise<void> => {
		if (!newName.trim()) {
			setRenameError('Session name cannot be empty');
			return;
		}

		if (newName.length > 20) {
			setRenameError('Session name cannot exceed 20 characters');
			return;
		}

		setIsRenaming(true);
		setRenameError(null);

		try {
			await ChatGateway.api.updateSessionName(sessionId, newName.trim());
			await refreshSessions();
			cancelRename();
		} catch (error: any) {
			if (error?.statusCode === 401) {
				danger('Unknown error. Redirecting to home.');
				navigate(screens.root, { replace: true });
				return;
			}
			const errorMessage = getErrorMessage(error);
			setRenameError(errorMessage);
		} finally {
			setIsRenaming(false);
		}
	};

	const openDeleteModal = (sessionId: string) => {
		setSessionToDelete(sessionId);
		setShowDeleteModal(true);
	};

	const closeDeleteModal = () => {
		setShowDeleteModal(false);
		setSessionToDelete(null);
	};

	const handleDeleteSession = async (): Promise<void> => {
		if (!sessionToDelete) return;

		const deletedId = sessionToDelete;

		try {
			await ChatGateway.api.deleteSession(deletedId);
			setSessions((prev) => prev.filter((s) => s.sessionId !== deletedId));
			await refreshSessions();
			closeDeleteModal();
			success('Session deleted successfully');
			if (onClearConversation) {
				onClearConversation();
			}
		} catch (error: any) {
			if (error?.statusCode === 401) {
				danger('Unknown error. Redirecting to home.');
				navigate(screens.root, { replace: true });
				return;
			}
			const errorMessage = getErrorMessage(error);
			danger(errorMessage);
		}
	};

	return {
		isExpanded,
		toggleSidebar,
		sessions,
		isLoading,
		totalMessages,
		totalTokens,
		sessionsCount: sessions.length,
		editingSessionId,
		editValue,
		isRenaming,
		startRename,
		cancelRename,
		saveRename,
		setEditValue,
		setRenameError,
		showDeleteModal,
		closeDeleteModal,
		handleDeleteSession,
		openDeleteModal,
		refreshSessions,
	};
};
